// src/pages/Billing.tsx
'use client';

import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { currentUser, getSupabase, signOut } from "@/lib/supabase";
import { ENV } from "@/lib/env";
import { format } from "date-fns";
import Sidebar from "@/components/Sidebar";
import { 
  CreditCard, CheckCircle2, AlertCircle, Loader2, Calendar, 
  ChevronLeft, Receipt, Home, Apple, Dumbbell, CalendarDays, 
  Utensils, Trophy, Settings, LogOut, Shield, Sparkles, 
  ExternalLink, Clock
} from "lucide-react";

export default function Billing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [status, setStatus] = useState<"active" | "past_due" | "cancelled" | "incomplete">("active");
  const [nextBillingDate, setNextBillingDate] = useState<string>("");
  const [plan, setPlan] = useState("Monthly");
  const [memberSince, setMemberSince] = useState<string>("");

  useEffect(() => {
    (async () => {
      const user = await currentUser();
      if (!user) {
        navigate("/login");
        return;
      }

      const { data, error } = await getSupabase()
        .from("clients")
        .select("subscription_status, current_period_end, plan_type, created_at")
        .eq("user_id", user.id)
        .single();

      if (error || !data) {
        console.error("Failed to load billing info:", error);
        setLoading(false);
        return;
      }

      setStatus(data.subscription_status || "cancelled");
      setPlan(data.plan_type || "Monthly");
      if (data.current_period_end) {
        setNextBillingDate(format(new Date(data.current_period_end), "d MMMM yyyy"));
      }
      if (data.created_at) {
        setMemberSince(format(new Date(data.created_at), "MMMM yyyy"));
      }
      setLoading(false);
    })();
  }, [navigate]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const user = await currentUser();
      if (!user) throw new Error("Not logged in");

      const res = await fetch(`${ENV.EDGE_BASE}/create-portal-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${(await getSupabase().auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({ user_id: user.id }),
      });

      const text = await res.text();
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${text}`);

      const data = JSON.parse(text);
      if (!data.url) throw new Error("No portal URL returned");

      window.location.href = data.url;
    } catch (err: any) {
      console.error("Portal failed:", err);
      alert("Failed to open billing portal — check console");
      setPortalLoading(false);
    }
  };

  const statusConfig = {
    active: { 
      color: "text-green-400", 
      bg: "bg-green-500/10", 
      border: "border-green-500/30", 
      gradient: "from-green-500/20 to-emerald-500/20",
      icon: CheckCircle2, 
      label: "Active",
      description: "Your subscription is active and in good standing."
    },
    past_due: { 
      color: "text-yellow-400", 
      bg: "bg-yellow-500/10", 
      border: "border-yellow-500/30", 
      gradient: "from-yellow-500/20 to-orange-500/20",
      icon: AlertCircle, 
      label: "Payment Issue",
      description: "There was a problem with your last payment. Please update your payment method."
    },
    cancelled: { 
      color: "text-red-400", 
      bg: "bg-red-500/10", 
      border: "border-red-500/30", 
      gradient: "from-red-500/20 to-pink-500/20",
      icon: AlertCircle, 
      label: "Cancelled",
      description: "Your subscription has been cancelled. Resubscribe to regain access."
    },
    incomplete: { 
      color: "text-red-400", 
      bg: "bg-red-500/10", 
      border: "border-red-500/30", 
      gradient: "from-red-500/20 to-pink-500/20",
      icon: AlertCircle, 
      label: "Incomplete",
      description: "Your subscription setup is incomplete. Please complete the payment."
    },
  };

  const config = statusConfig[status] || statusConfig.cancelled;
  const Icon = config.icon;

  if (loading) {
    return (
      <div className="flex h-screen bg-[#ffffff]">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-10 h-10 text-green-400 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#ffffff] text-[#0f172a]">
      <Sidebar />
      
      <main className="flex-1 overflow-y-auto">
        {/* Header */}
        <header className="sticky top-0 z-10 bg-[#ffffff] border-b border-[#e2e8f0] px-4 py-4 md:px-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="md:hidden p-2 bg-[#f1f5f9] hover:bg-[#f1f5f9] rounded-lg">
              <Home className="w-5 h-5 text-gray-500" />
            </Link>
            <button onClick={() => navigate(-1)} className="hidden md:block">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl md:text-3xl font-bold flex items-center gap-2">
              <CreditCard className="w-6 h-6 md:w-8 md:h-8 text-green-400" />
              Billing
            </h1>
          </div>
        </header>

        <div className="p-4 md:p-6 max-w-3xl mx-auto">
          {/* Status Card */}
          <div className={`bg-gradient-to-br ${config.gradient} border ${config.border} rounded-2xl p-5 md:p-6 mb-6`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl ${config.bg} flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 md:w-7 md:h-7 ${config.color}`} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Status</p>
                  <p className={`text-xl md:text-2xl font-bold ${config.color}`}>{config.label}</p>
                </div>
              </div>
              {status === 'active' && (
                <div className="hidden md:flex items-center gap-1 px-3 py-1 bg-green-500/20 rounded-full">
                  <Sparkles className="w-4 h-4 text-green-400" />
                  <span className="text-xs text-green-400 font-medium">Premium</span>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500">{config.description}</p>
          </div>

          {/* Plan Details */}
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-5 md:p-6 mb-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-gray-500" />
              Plan Details
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-[#e2e8f0]">
                <span className="text-gray-500 text-sm">Current Plan</span>
                <div className="text-right">
                  <span className="font-bold text-[#0f172a]">{plan}</span>
                  <span className="text-gray-500 ml-1">– £12.99/month</span>
                </div>
              </div>
              
              {status === "active" && nextBillingDate && (
                <div className="flex items-center justify-between py-3 border-b border-[#e2e8f0]">
                  <span className="text-gray-500 text-sm flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Next Billing Date
                  </span>
                  <span className="font-bold text-[#0f172a]">{nextBillingDate}</span>
                </div>
              )}
              
              {memberSince && (
                <div className="flex items-center justify-between py-3">
                  <span className="text-gray-500 text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Member Since
                  </span>
                  <span className="font-medium text-gray-600">{memberSince}</span>
                </div>
              )}
            </div>
          </div>

          {/* What's Included */}
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-5 md:p-6 mb-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              What's Included
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "24/7 AI Nutrition Coaching",
                "Personalised Meal Plans",
                "Custom Training Programs",
                "Garmin & WHOOP Integration",
                "Progress Tracking",
                "Challenges & Leaderboards"
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-gray-600">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Manage Billing Button */}
          <button
            onClick={openPortal}
            disabled={portalLoading}
            className="w-full bg-green-500 hover:bg-green-400 disabled:opacity-60 disabled:hover:bg-green-500 text-[#0f172a] font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-green-500/20"
          >
            {portalLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Opening secure portal...
              </>
            ) : (
              <>
                Manage Billing
                <ExternalLink className="w-5 h-5" />
              </>
            )}
          </button>

          <p className="text-center text-gray-500 mt-3 text-xs">
            Update payment method • View invoices • Cancel anytime
          </p>

          {/* Security Note */}
          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-500">
            <Shield className="w-4 h-4" />
            <span>Secure billing powered by <span className="text-[#0f172a] font-medium">Stripe</span></span>
          </div>
        </div>
      </main>
    </div>
  );
}
